import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowDown, ArrowUp, Check, ChevronRight, Clapperboard, EyeOff, Film, ImagePlus, Link2,
  Loader2, MousePointerClick, Plus, Send, Trash2, Upload, Video, X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { toApiError } from '@/lib/api';
import { useDeleteStory, usePublishStory, useSaveStory, useStory, useUploadMedia } from '@/lib/queries';
import { useAutosave } from '@/hooks/useAutosave';
import { useToast } from '@/components/ui/Toast';
import type {
  MediaItem, StoryDetail, StoryInput, StoryLocale, StoryMediaType, StorySlideInput,
} from '@/lib/types';
import { STORY_LOCALES } from '@/lib/types';
import { Segmented } from '@/components/MediaPicker';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Select, Switch, Tooltip } from '@/components/ui/primitives';
import { Skeleton } from '@/components/ui/Feedback';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';
const MEDIA_ACCEPT = `${IMAGE_ACCEPT},video/mp4`;

const TTL_OPTIONS = [12, 24, 36, 48] as const;
const DEFAULT_TTL = 24;
const DEFAULT_DURATION = 5;

/**
 * Placeholders written IN the target language, not the dashboard's UI language:
 * switching the tab to «RU» must visibly ask for Russian text, so the empty
 * field itself says "type here, in this language". Keyed by content locale, so
 * they read the same whether the UI is uz or ru.
 */
const LOCALE_PLACEHOLDERS: Record<StoryLocale, { title: string; caption: string; linkLabel: string }> = {
  uz: { title: 'Sarlavhani o‘zbekcha kiriting', caption: 'Slayd matnini o‘zbekcha yozing', linkLabel: 'Tugma matni (o‘zbekcha)' },
  ru: { title: 'Введите заголовок на русском', caption: 'Текст слайда на русском', linkLabel: 'Текст кнопки (на русском)' },
  en: { title: 'Enter the title in English', caption: 'Slide text in English', linkLabel: 'Button label (in English)' },
  zh: { title: '请输入中文标题', caption: '用中文输入幻灯片文字', linkLabel: '按钮文字（中文）' },
};

interface SlideDraft {
  key: string;
  mediaType: StoryMediaType;
  mediaId: string | null;
  mediaUrl: string | null;
  posterId: string | null;
  posterUrl: string | null;
  caption: Record<StoryLocale, string>;
  // The bottom CTA button is opt-in per slide. `buttonOn` is a client-only flag:
  // turning it off HIDES the fields but keeps the typed link/label in the draft,
  // so toggling back on restores them. It's persisted only via linkUrl on save.
  buttonOn: boolean;
  linkUrl: string;
  linkLabel: Record<StoryLocale, string>;
  durationSec: number;
}

interface Draft {
  title: Record<StoryLocale, string>;
  coverId: string | null;
  coverUrl: string | null;
  ttlHours: number;
  order: number;
  slides: SlideDraft[];
}

const emptyDraft = (): Draft => ({
  title: { uz: '', ru: '', en: '', zh: '' },
  coverId: null,
  coverUrl: null,
  ttlHours: DEFAULT_TTL,
  order: 0,
  slides: [],
});

let slideKeySeq = 0;
const newSlideKey = () => `slide-${slideKeySeq++}`;

const emptySlide = (): SlideDraft => ({
  key: newSlideKey(),
  mediaType: 'IMAGE',
  mediaId: null,
  mediaUrl: null,
  posterId: null,
  posterUrl: null,
  caption: { uz: '', ru: '', en: '', zh: '' },
  buttonOn: false,
  linkUrl: '',
  linkLabel: { uz: '', ru: '', en: '', zh: '' },
  durationSec: DEFAULT_DURATION,
});

/**
 * The ring editor lives in a modal, not a page: creating a story is a quick,
 * focused act, and staff never lose the tray behind it. The modal is split —
 * the form on the left, a live phone preview on the right that renders exactly
 * what the app will show, updating as you type. That preview is the point: an
 * SMM sees the story, not a database form.
 */
export function StoryEditorModal({
  storyId,
  onClose,
}: {
  /** A ring id to edit, or 'new' to create. `null` keeps the modal closed. */
  storyId: string | 'new' | null;
  onClose: () => void;
}) {
  const isNew = storyId === 'new';
  const { data, isLoading } = useStory(!storyId || isNew ? undefined : storyId);
  const save = useSaveStory();
  const publish = usePublishStory();
  const remove = useDeleteStory();
  const { t, lang } = useI18n();
  const toast = useToast();

  const [editId, setEditId] = useState<string | 'new'>(isNew ? 'new' : (storyId ?? 'new'));
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [locale, setLocale] = useState<StoryLocale>(lang === 'ru' ? 'ru' : 'uz');
  const [active, setActive] = useState(0); // slide shown in the preview
  const [confirmDelete, setConfirmDelete] = useState(false);

  const story = data?.item;
  const isPublished = story?.status === 'PUBLISHED';

  // Reset when the target ring changes (modal re-opened for another/new story).
  useEffect(() => {
    setEditId(isNew ? 'new' : (storyId ?? 'new'));
    setActive(0);
    setConfirmDelete(false);
    if (isNew) setDraft(emptyDraft());
  }, [storyId, isNew]);

  useEffect(() => {
    if (story) setDraft(toDraft(story));
  }, [story]);

  const autosave = useAutosave({
    value: draft,
    enabled: !isLoading,
    auto: false,
    onSave: async (value) => {
      const input = toInput(value);
      const patch = editId === 'new' ? input : { ...input, expectedUpdatedAt: story?.updatedAt };
      const result = await save.mutateAsync({ id: editId, input: patch });
      if (editId === 'new') setEditId(result.item.id);
    },
  });

  useEffect(() => {
    if (autosave.state !== 'error' || !save.error) return;
    const error = toApiError(save.error);
    toast({ tone: 'error', message: error.code === 'CONFLICT' ? t.editor.conflictTitle : error.message });
  }, [autosave.state, save.error, toast, t.editor.conflictTitle]);

  const canPublish = useMemo(
    () => draft.slides.length > 0 && draft.slides.every((s) => Boolean(s.mediaId)),
    [draft.slides],
  );

  // Keep the previewed slide index in range as slides are added/removed.
  const activeIndex = Math.min(active, Math.max(0, draft.slides.length - 1));

  const setTitle = (value: string) =>
    setDraft((c) => ({ ...c, title: { ...c.title, [locale]: value } }));
  const updateSlide = (index: number, patch: Partial<SlideDraft>) =>
    setDraft((c) => ({ ...c, slides: c.slides.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));
  const setSlideCaption = (index: number, value: string) =>
    updateSlide(index, { caption: { ...draft.slides[index]!.caption, [locale]: value } });
  const setSlideLinkLabel = (index: number, value: string) =>
    updateSlide(index, { linkLabel: { ...draft.slides[index]!.linkLabel, [locale]: value } });
  const addSlide = () =>
    setDraft((c) => {
      const slides = [...c.slides, emptySlide()];
      setActive(slides.length - 1);
      return { ...c, slides };
    });
  const removeSlide = (index: number) =>
    setDraft((c) => ({ ...c, slides: c.slides.filter((_, i) => i !== index) }));
  const moveSlide = (index: number, dir: -1 | 1) =>
    setDraft((c) => {
      const target = index + dir;
      if (target < 0 || target >= c.slides.length) return c;
      const slides = [...c.slides];
      [slides[index], slides[target]] = [slides[target]!, slides[index]!];
      setActive(target);
      return { ...c, slides };
    });

  const setSlideMedia = (index: number, item: MediaItem) => {
    const mediaType: StoryMediaType = item.mime.startsWith('video/') ? 'VIDEO' : 'IMAGE';
    updateSlide(index, { mediaId: item.id, mediaUrl: item.url, mediaType });
    setActive(index);
  };
  const setSlidePoster = (index: number, item: MediaItem) =>
    updateSlide(index, { posterId: item.id, posterUrl: item.url });

  const close = async () => {
    // Never lose work: an unsaved edit is flushed as a draft on the way out.
    if (autosave.dirty) await autosave.flush();
    onClose();
  };

  const onPublish = async () => {
    await autosave.flush();
    try {
      await publish.mutateAsync({ id: editId as string, publish: true });
      autosave.markSaved();
      toast({ tone: 'success', message: t.stories.published });
      onClose();
    } catch (error) {
      toast({ tone: 'error', message: toApiError(error).message });
    }
  };

  const title = draft.title[locale] || draft.title.uz || t.stories.new;

  return (
    <Dialog.Root open={storyId !== null} onOpenChange={(open) => !open && void close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/55 backdrop-blur-[3px] data-[state=open]:animate-[fade-rise_160ms_var(--ease-out)]" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'modal-anim fixed left-1/2 top-1/2 z-50 flex h-[calc(100dvh-2.5rem)] max-h-[54rem] w-[calc(100vw-2rem)] max-w-[72rem]',
            '-translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[1.25rem] border border-line bg-surface',
            'shadow-[0_24px_80px_-24px_rgb(18_22_55_/_0.55)]',
          )}
        >
          {/* Header */}
          <header className="flex items-center gap-2 border-b border-line px-3 py-2.5 sm:gap-3 sm:px-5 sm:py-3">
            <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-500/10 text-brand-600 dark:text-brand-300 sm:flex">
              <Clapperboard className="h-[1.125rem] w-[1.125rem]" strokeWidth={1.9} />
            </span>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="truncate font-display text-[1.0625rem] font-bold text-strong">
                {title}
              </Dialog.Title>
              <p className="text-[0.6875rem] text-faint">
                {isNew ? t.stories.new : isPublished ? t.stories.statusLive : t.stories.statusDraft}
                {' · '}
                {t.stories.slideCount(draft.slides.length)}
              </p>
            </div>

            <Segmented
              value={locale}
              onChange={setLocale}
              options={STORY_LOCALES.map((l) => ({ value: l, label: l.toUpperCase() }))}
            />

            <Button
              size="sm"
              variant="primary"
              icon={<Check className="h-4 w-4" strokeWidth={2} />}
              loading={autosave.state === 'saving'}
              disabled={!autosave.dirty || autosave.state === 'saving'}
              onClick={async () => {
                const ok = await autosave.flush();
                if (ok) toast({ tone: 'success', message: t.editor.saveOk });
              }}
            >
              {/* Labels fold away below sm — the icons carry the buttons on a phone. */}
              <span className="hidden sm:inline">{autosave.dirty ? t.common.save : t.common.saved}</span>
            </Button>

            {isPublished ? (
              <Tooltip label={t.stories.unpublishHint} side="bottom">
                <Button
                  size="sm"
                  variant="outline"
                  icon={<EyeOff className="h-4 w-4" strokeWidth={1.85} />}
                  loading={publish.isPending}
                  onClick={() => void publish.mutateAsync({ id: editId as string, publish: false })}
                >
                  <span className="hidden sm:inline">{t.stories.unpublish}</span>
                </Button>
              </Tooltip>
            ) : (
              <Tooltip label={canPublish ? t.stories.publish : t.stories.publishBlocked} side="bottom">
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<Send className="h-4 w-4" strokeWidth={1.85} />}
                    disabled={editId === 'new' || !canPublish}
                    onClick={onPublish}
                    loading={publish.isPending}
                  >
                    <span className="hidden sm:inline">{t.stories.publish}</span>
                  </Button>
                </span>
              </Tooltip>
            )}

            {editId !== 'new' && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={t.common.delete}
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4 text-accent-600 dark:text-accent-400" strokeWidth={1.85} />
              </Button>
            )}
            <Dialog.Close
              aria-label={t.common.close}
              className="cursor-pointer rounded-control p-2 text-muted transition-ui duration-instant hover:bg-raised hover:text-strong"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </header>

          {/* Body: form + live preview */}
          {isLoading ? (
            <div className="grid flex-1 grid-cols-[minmax(0,1fr)_320px] gap-6 p-6">
              <Skeleton className="h-full w-full rounded-card" />
              <Skeleton className="h-full w-full rounded-[2rem]" />
            </div>
          ) : (
            <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
              {/* Form column */}
              <div className="min-w-0 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
                <Field label={t.stories.titleLabel} hint={t.stories.titleHint}>
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      value={draft.title[locale]}
                      onChange={(e) => setTitle(e.target.value)}
                      className="h-11 font-display text-[1.25rem] font-bold"
                      placeholder={LOCALE_PLACEHOLDERS[locale].title}
                    />
                  )}
                </Field>

                <div className="mt-6 flex items-center justify-between">
                  <h3 className="text-eyebrow uppercase text-faint">{t.stories.slidesGroup}</h3>
                  <Button size="sm" variant="subtle" icon={<Plus className="h-4 w-4" />} onClick={addSlide}>
                    {t.stories.addSlide}
                  </Button>
                </div>

                {draft.slides.length === 0 ? (
                  <button
                    type="button"
                    onClick={addSlide}
                    className="mt-3 flex w-full flex-col items-center gap-2 rounded-card border border-dashed border-line-strong px-4 py-10 text-muted transition-ui duration-instant hover:border-brand-500 hover:text-strong"
                  >
                    <ImagePlus className="h-7 w-7" strokeWidth={1.6} />
                    <span className="text-ui font-medium">{t.stories.noSlides}</span>
                  </button>
                ) : (
                  <div className="mt-3 flex flex-col gap-3">
                    {draft.slides.map((slide, index) => (
                      <SlideCard
                        key={slide.key}
                        slide={slide}
                        index={index}
                        total={draft.slides.length}
                        locale={locale}
                        active={index === activeIndex}
                        onSelect={() => setActive(index)}
                        onMove={(dir) => moveSlide(index, dir)}
                        onRemove={() => removeSlide(index)}
                        onUploadedMedia={(item) => setSlideMedia(index, item)}
                        onUploadedPoster={(item) => setSlidePoster(index, item)}
                        onClearMedia={() => updateSlide(index, { mediaId: null, mediaUrl: null, mediaType: 'IMAGE' })}
                        onClearPoster={() => updateSlide(index, { posterId: null, posterUrl: null })}
                        onCaption={(v) => setSlideCaption(index, v)}
                        onLinkUrl={(v) => updateSlide(index, { linkUrl: v })}
                        onLinkLabel={(v) => setSlideLinkLabel(index, v)}
                        onToggleButton={(on) => updateSlide(index, { buttonOn: on })}
                      />
                    ))}
                  </div>
                )}

                {/* Cover + TTL */}
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-eyebrow uppercase text-faint">{t.stories.coverLabel}</h3>
                    {draft.coverUrl ? (
                      <div className="flex items-center gap-3">
                        <span className="h-16 w-16 shrink-0 overflow-hidden rounded-full border border-line">
                          <img src={draft.coverUrl} alt="" className="h-full w-full object-cover" />
                        </span>
                        <div className="flex flex-col items-start gap-1">
                          <UploadButton
                            label={t.stories.mediaChange}
                            size="sm"
                            variant="ghost"
                            onUploaded={(item) => setDraft((c) => ({ ...c, coverId: item.id, coverUrl: item.url }))}
                          />
                          <button
                            type="button"
                            onClick={() => setDraft((c) => ({ ...c, coverId: null, coverUrl: null }))}
                            className="cursor-pointer text-[0.6875rem] text-muted hover:text-accent-600 dark:hover:text-accent-400"
                          >
                            {t.editor.coverRemove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <UploadButton
                        label={t.editor.coverEmpty}
                        icon={<ImagePlus className="h-4 w-4" />}
                        variant="outline"
                        className="border-dashed"
                        onUploaded={(item) => setDraft((c) => ({ ...c, coverId: item.id, coverUrl: item.url }))}
                      />
                    )}
                    <p className="mt-1.5 text-[0.6875rem] text-faint">{t.stories.coverHint}</p>
                  </div>

                  <Field label={t.stories.ttlLabel} hint={t.stories.ttlHint}>
                    {({ id }) => (
                      <Select
                        id={id}
                        value={String(draft.ttlHours)}
                        onValueChange={(v) => setDraft((c) => ({ ...c, ttlHours: Number(v) }))}
                        options={TTL_OPTIONS.map((n) => ({ value: String(n), label: t.stories.ttlOption(n) }))}
                      />
                    )}
                  </Field>
                </div>
              </div>

              {/* Live preview column */}
              <aside className="hidden min-h-0 flex-col items-center gap-4 border-l border-line bg-raised/40 px-6 py-6 lg:flex">
                <p className="text-eyebrow uppercase text-faint">{t.stories.previewLabel}</p>
                <StoryPreview
                  draft={draft}
                  activeIndex={activeIndex}
                  locale={locale}
                  onSelect={setActive}
                  labels={{ now: t.stories.previewNow, empty: t.stories.previewEmpty }}
                />
                <p className="mt-auto text-center text-[0.6875rem] leading-relaxed text-faint">
                  {t.stories.previewHint}
                </p>
              </aside>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>

      {/* Delete confirm */}
      <Dialog.Root open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-ink-900/55 backdrop-blur-[2px]" />
          <Dialog.Content className="modal-anim card fixed left-1/2 top-1/2 z-[70] w-[calc(100vw-2rem)] max-w-[26rem] -translate-x-1/2 -translate-y-1/2 overflow-hidden p-6 shadow-pop">
            <Dialog.Title className="font-display text-section text-strong">{t.stories.deleteConfirm}</Dialog.Title>
            <Dialog.Description className="mt-1 text-ui text-muted">{t.stories.deleteConfirmBody}</Dialog.Description>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(false)}>{t.common.cancel}</Button>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={async () => {
                  if (editId === 'new') return;
                  await remove.mutateAsync(editId);
                  toast({ tone: 'info', message: t.common.deleted });
                  setConfirmDelete(false);
                  onClose();
                }}
              >
                {t.common.delete}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Dialog.Root>
  );
}

/** The Instagram-style phone preview — the same frames the app will paint. */
function StoryPreview({
  draft, activeIndex, locale, onSelect, labels,
}: {
  draft: Draft;
  activeIndex: number;
  locale: StoryLocale;
  onSelect: (index: number) => void;
  labels: { now: string; empty: string };
}) {
  const slides = draft.slides;
  const slide = slides[activeIndex];
  const title = draft.title[locale] || draft.title.uz || '';
  const caption = slide?.caption[locale] || slide?.caption.uz || '';
  const label = slide?.linkLabel[locale] || slide?.linkLabel.uz || '';
  const avatar = draft.coverUrl || slide?.mediaUrl || null;

  return (
    <div className="relative aspect-[9/19] w-[248px] shrink-0 overflow-hidden rounded-[2.25rem] border-[6px] border-ink-900 bg-ink-900 shadow-[0_18px_50px_-16px_rgb(18_22_55_/_0.6)]">
      {/* Media */}
      {slide?.mediaUrl ? (
        slide.mediaType === 'VIDEO' ? (
          <video src={slide.mediaUrl} muted playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <img src={slide.mediaUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
        )
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-ink-700 to-ink-900 text-white/50">
          <div className="flex flex-col items-center gap-2 px-6 text-center">
            <Film className="h-8 w-8" strokeWidth={1.5} />
            <span className="text-[0.75rem]">{labels.empty}</span>
          </div>
        </div>
      )}

      {/* Scrims for legibility */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-ink-900/70 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink-900/85 to-transparent" />

      {/* Progress bars — one per slide, tap to preview */}
      <div className="absolute inset-x-3 top-3 flex gap-1">
        {(slides.length ? slides : [null]).map((s, i) => (
          <button
            key={s ? (s as SlideDraft).key : 'empty'}
            type="button"
            onClick={() => onSelect(i)}
            className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/30"
            aria-label={`${i + 1}`}
          >
            <span className={cn('block h-full rounded-full bg-white transition-all', i < activeIndex ? 'w-full' : i === activeIndex ? 'w-full' : 'w-0')} />
          </button>
        ))}
      </div>

      {/* Header: avatar + title */}
      <div className="absolute inset-x-3 top-7 flex items-center gap-2">
        <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full ring-2 ring-white/90">
          {avatar ? (
            <img src={avatar} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-center justify-center bg-white/20 text-white">
              <Clapperboard className="h-4 w-4" />
            </span>
          )}
        </span>
        <span className="truncate text-[0.8125rem] font-semibold text-white drop-shadow">{title || 'iPost'}</span>
        <span className="text-[0.6875rem] text-white/70">{labels.now}</span>
      </div>

      {/* Caption + CTA */}
      <div className="absolute inset-x-4 bottom-5 flex flex-col items-center gap-3 text-center">
        {caption && (
          <p className="text-[0.9375rem] font-semibold leading-snug text-white drop-shadow-[0_1px_6px_rgb(0_0_0_/_0.5)]">
            {caption}
          </p>
        )}
        {slide?.buttonOn && (
          <span className="flex w-full items-center justify-center gap-1.5 rounded-full bg-white px-4 py-2.5 text-[0.8125rem] font-bold text-ink-900 shadow-lg">
            {label || 'Batafsil'}
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Browse-and-upload straight from the computer — no shared gallery. Story media
 * must not mingle with website images, so the picker that listed every site
 * asset is gone; each slot uploads its own file (the backend de-dupes by hash).
 */
function UploadButton({
  label, icon, onUploaded, allowVideo = false, variant = 'outline', size, className,
}: {
  label: string;
  icon?: React.ReactNode;
  onUploaded: (item: MediaItem) => void;
  allowVideo?: boolean;
  variant?: 'primary' | 'outline' | 'ghost' | 'subtle';
  size?: 'sm';
  className?: string;
}) {
  const upload = useUploadMedia();
  const toast = useToast();
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        icon={upload.isPending ? undefined : icon}
        loading={upload.isPending}
        onClick={() => ref.current?.click()}
      >
        {label}
      </Button>
      <input
        ref={ref}
        type="file"
        accept={allowVideo ? MEDIA_ACCEPT : IMAGE_ACCEPT}
        className="sr-only"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          try {
            const { item } = await upload.mutateAsync(file);
            onUploaded(item);
          } catch (err) {
            toast({ tone: 'error', message: toApiError(err).message });
          }
        }}
      />
    </>
  );
}

function SlideCard({
  slide, index, total, locale, active,
  onSelect, onMove, onRemove, onUploadedMedia, onUploadedPoster, onClearMedia, onClearPoster,
  onCaption, onLinkUrl, onLinkLabel, onToggleButton,
}: {
  slide: SlideDraft;
  index: number;
  total: number;
  locale: StoryLocale;
  active: boolean;
  onSelect: () => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  onUploadedMedia: (item: MediaItem) => void;
  onUploadedPoster: (item: MediaItem) => void;
  onClearMedia: () => void;
  onClearPoster: () => void;
  onCaption: (v: string) => void;
  onLinkUrl: (v: string) => void;
  onLinkLabel: (v: string) => void;
  onToggleButton: (on: boolean) => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const upload = useUploadMedia();
  const mediaInput = useRef<HTMLInputElement>(null);
  const isVideo = slide.mediaType === 'VIDEO';
  const buttonOn = slide.buttonOn;

  const pickMedia = async (file?: File) => {
    if (!file) return;
    try {
      const { item } = await upload.mutateAsync(file);
      onUploadedMedia(item);
    } catch (err) {
      toast({ tone: 'error', message: toApiError(err).message });
    }
  };

  return (
    <div
      className={cn(
        'rounded-card border bg-surface p-3.5 transition-ui duration-instant',
        active ? 'border-brand-500 ring-1 ring-brand-500/25' : 'border-line hover:border-line-strong',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={onSelect} className="cursor-pointer text-ui font-semibold text-strong">
          {t.stories.slideN(index + 1)}
        </button>
        <div className="flex items-center gap-0.5">
          <Button size="sm" variant="ghost" aria-label={t.stories.moveUp} disabled={index === 0} onClick={() => onMove(-1)}>
            <ArrowUp className="h-4 w-4" strokeWidth={1.85} />
          </Button>
          <Button size="sm" variant="ghost" aria-label={t.stories.moveDown} disabled={index === total - 1} onClick={() => onMove(1)}>
            <ArrowDown className="h-4 w-4" strokeWidth={1.85} />
          </Button>
          <Button size="sm" variant="ghost" aria-label={t.stories.removeSlide} onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-accent-600 dark:text-accent-400" strokeWidth={1.85} />
          </Button>
        </div>
      </div>

      <div className="mt-3 flex gap-3.5">
        {/* Media tile — click to browse a file from the computer. */}
        <div className="relative w-24 shrink-0">
          <button
            type="button"
            onClick={() => mediaInput.current?.click()}
            className={cn(
              'group relative block aspect-[9/16] w-full overflow-hidden rounded-control border bg-raised transition-ui duration-instant',
              slide.mediaUrl ? 'border-line' : 'border-dashed border-line-strong hover:border-brand-500',
            )}
          >
            {slide.mediaUrl ? (
              isVideo ? (
                <video src={slide.mediaUrl} muted playsInline preload="metadata" className="h-full w-full object-cover" />
              ) : (
                <img src={slide.mediaUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
              )
            ) : (
              <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-1 text-center text-faint transition-colors group-hover:text-brand-500">
                <Upload className="h-5 w-5" strokeWidth={1.6} />
                <span className="text-[0.625rem] font-medium leading-tight">{t.stories.uploadMedia}</span>
              </span>
            )}

            {upload.isPending && (
              <span className="absolute inset-0 flex items-center justify-center bg-ink-900/50">
                <Loader2 className="h-5 w-5 animate-spin text-white" />
              </span>
            )}
            {slide.mediaUrl && !upload.isPending && (
              <span className="absolute inset-0 flex items-center justify-center bg-ink-900/0 opacity-0 transition-opacity duration-instant group-hover:bg-ink-900/45 group-hover:opacity-100">
                <span className="rounded-chip bg-surface px-2 py-0.5 text-[0.625rem] font-semibold text-strong">{t.stories.mediaChange}</span>
              </span>
            )}
            {isVideo && slide.mediaUrl && (
              <span className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 rounded-chip bg-ink-900/70 px-1.5 py-0.5 text-[0.5625rem] font-medium text-white">
                <Video className="h-2.5 w-2.5" /> {t.stories.mediaTypeVideo}
              </span>
            )}
          </button>
          {slide.mediaUrl && (
            <button
              type="button"
              onClick={onClearMedia}
              aria-label={t.editor.coverRemove}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-line bg-surface text-muted shadow-card transition-ui duration-instant hover:text-accent-600 dark:hover:text-accent-400"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <input
            ref={mediaInput}
            type="file"
            accept={MEDIA_ACCEPT}
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              void pickMedia(file);
            }}
          />
        </div>

        {/* Caption + (for video) a poster still. */}
        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <Field label={t.stories.captionLabel} hint={t.stories.captionHint}>
            {({ id, describedBy }) => (
              <Input id={id} aria-describedby={describedBy} value={slide.caption[locale]} placeholder={LOCALE_PLACEHOLDERS[locale].caption} onChange={(e) => onCaption(e.target.value)} />
            )}
          </Field>
          {isVideo && (
            <div className="flex items-center gap-2">
              <UploadButton
                label={slide.posterUrl ? t.stories.mediaChange : t.stories.posterEmpty}
                icon={<ImagePlus className="h-4 w-4" />}
                size="sm"
                onUploaded={onUploadedPoster}
              />
              {slide.posterUrl && (
                <Button size="sm" variant="ghost" aria-label={t.editor.coverRemove} onClick={onClearPoster}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
              <span className="text-[0.6875rem] text-faint">{t.stories.posterLabel}</span>
            </div>
          )}
        </div>
      </div>

      {/* Optional bottom CTA button. The switch shows/hides it; turning it off
          only HIDES the fields (the typed link/label stay in the draft, so
          toggling back on restores them) and is excluded from the save. */}
      <div
        className={cn(
          'mt-4 rounded-control border px-3 py-3 transition-ui duration-instant',
          buttonOn ? 'border-brand-500/40 bg-brand-500/[0.04]' : 'border-line bg-transparent',
        )}
      >
        <button
          type="button"
          onClick={() => onToggleButton(!buttonOn)}
          className="flex w-full cursor-pointer items-center justify-between gap-2"
        >
          <span className="flex min-w-0 items-center gap-2">
            <MousePointerClick aria-hidden className="h-4 w-4 shrink-0 text-brand-500 dark:text-brand-300" strokeWidth={1.85} />
            <span className="text-eyebrow uppercase text-faint">{t.stories.buttonGroup}</span>
            <span className="truncate text-[0.6875rem] text-faint">· {t.stories.buttonHint}</span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <span className={cn('text-[0.6875rem] font-semibold', buttonOn ? 'text-brand-600 dark:text-brand-300' : 'text-faint')}>
              {buttonOn ? t.stories.buttonOn : t.stories.buttonOff}
            </span>
            <Switch checked={buttonOn} onCheckedChange={onToggleButton} aria-label={t.stories.buttonGroup} />
          </span>
        </button>
        {buttonOn && (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t.stories.linkUrlLabel}>
              {({ id, describedBy }) => (
                <div className="relative">
                  <Link2 aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
                  <Input id={id} aria-describedby={describedBy} value={slide.linkUrl} placeholder="https://…" className="pl-9" onChange={(e) => onLinkUrl(e.target.value)} />
                </div>
              )}
            </Field>
            <Field label={t.stories.linkLabelLabel}>
              {({ id, describedBy }) => (
                <Input id={id} aria-describedby={describedBy} value={slide.linkLabel[locale]} placeholder={LOCALE_PLACEHOLDERS[locale].linkLabel} onChange={(e) => onLinkLabel(e.target.value)} />
              )}
            </Field>
            {!slide.linkUrl.trim() && (
              <p className="text-[0.6875rem] text-accent-600 dark:text-accent-400 sm:col-span-2">{t.stories.buttonNeedsUrl}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function toDraft(story: StoryDetail): Draft {
  return {
    title: {
      uz: story.title.uz ?? '', ru: story.title.ru ?? '', en: story.title.en ?? '', zh: story.title.zh ?? '',
    },
    coverId: story.coverId,
    coverUrl: story.cover?.url ?? null,
    ttlHours: story.ttlHours,
    order: story.order,
    slides: [...story.slides]
      .sort((a, b) => a.order - b.order)
      .map((s) => ({
        key: newSlideKey(),
        mediaType: s.mediaType,
        mediaId: s.mediaId,
        mediaUrl: s.media?.url ?? null,
        posterId: s.posterId,
        posterUrl: s.poster?.url ?? null,
        caption: {
          uz: s.caption.uz ?? '', ru: s.caption.ru ?? '', en: s.caption.en ?? '', zh: s.caption.zh ?? '',
        },
        buttonOn: Boolean(s.linkUrl),
        linkUrl: s.linkUrl ?? '',
        linkLabel: {
          uz: s.linkLabel.uz ?? '', ru: s.linkLabel.ru ?? '', en: s.linkLabel.en ?? '', zh: s.linkLabel.zh ?? '',
        },
        durationSec: s.durationSec,
      })),
  };
}

function toInput(draft: Draft): StoryInput {
  const slides: StorySlideInput[] = draft.slides.map((s) => ({
    mediaType: s.mediaType,
    mediaId: s.mediaId,
    posterId: s.mediaType === 'VIDEO' ? s.posterId : null,
    captionUz: s.caption.uz || null,
    captionRu: s.caption.ru || null,
    captionEn: s.caption.en || null,
    captionZh: s.caption.zh || null,
    // Only persist the button when it's toggled on; off ⇒ no link at all.
    linkUrl: s.buttonOn ? s.linkUrl.trim() || null : null,
    linkLabelUz: s.buttonOn ? s.linkLabel.uz || null : null,
    linkLabelRu: s.buttonOn ? s.linkLabel.ru || null : null,
    linkLabelEn: s.buttonOn ? s.linkLabel.en || null : null,
    linkLabelZh: s.buttonOn ? s.linkLabel.zh || null : null,
    durationSec: s.durationSec,
  }));
  return {
    titleUz: draft.title.uz || null,
    titleRu: draft.title.ru || null,
    titleEn: draft.title.en || null,
    titleZh: draft.title.zh || null,
    coverId: draft.coverId,
    order: draft.order,
    ttlHours: draft.ttlHours,
    slides,
  };
}
